// Admin Supplier Management Page
import React, { useState } from 'react';
import { suppliers, supplierCategories } from '../../lib/suppliers';
import { getSupplierSpendingSummary } from '../../lib/productCosts';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ExternalLink, MapPin, Phone, Mail, Package, Euro,
  Truck, Star, Search, Filter, Coins, Lightbulb, BookOpen
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function SuppliersManagementPage() {
  const { isAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const spendingSummary = getSupplierSpendingSummary();

  if (!isAdmin) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
            <p>You need admin privileges to view this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch =
      supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || supplier.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const getPriceRangeLevel = (range) => {
    const map = {
      '€': 1,
      '€€': 2,
      '€€€': 3,
      '€€€€': 4
    };
    return map[range] || 1;
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-5xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Supplier Management
          </h1>
          <p className="text-xl text-muted-foreground">
            Munich & Bavaria Region - {suppliers.length} suppliers
          </p>
        </div>

        {/* Spending Summary */}
        <Card className="mb-8 backdrop-blur-sm bg-card/70 border-2 border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Euro className="h-6 w-6" />
              Supplier Spending Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(spendingSummary)
                .sort((a, b) => b[1].totalSpending - a[1].totalSpending)
                .slice(0, 3)
                .map(([supplierId, data]) => (
                  <div key={supplierId} className="p-4 rounded-lg bg-background/60 border border-border/60">
                    <p className="font-semibold text-lg mb-1">{data.supplierName}</p>
                    <p className="text-2xl font-bold text-primary">
                      €{data.totalSpending.toFixed(2)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {data.items.length} products
                    </p>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
            <Input
              type="text"
              placeholder="Search suppliers by name, location, or specialty..."
              className="pl-10 backdrop-blur-sm bg-background/70"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 rounded-lg border-2 border-border backdrop-blur-sm bg-background/70"
          >
            <option value="all">All Categories</option>
            {Object.entries(supplierCategories).map(([key, value]) => (
              <option key={value} value={value}>
                {key.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        {/* Results Count */}
        <p className="mb-4 text-muted-foreground">
          Showing <span className="font-bold text-primary">{filteredSuppliers.length}</span> suppliers
        </p>

        {/* Suppliers Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredSuppliers.map((supplier) => (
            <Card
              key={supplier.id}
              className="overflow-hidden backdrop-blur-sm bg-card/70 border-2 border-border hover:shadow-2xl transition-all duration-300"
            >
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-2xl mb-2">{supplier.name}</CardTitle>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <MapPin className="h-4 w-4" />
                      {supplier.location}
                    </div>
                    <Badge className="bg-primary text-primary-foreground">
                      {supplier.type}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <div className="flex justify-end gap-1 mb-1">
                      {Array.from({ length: getPriceRangeLevel(supplier.priceRange) }).map((_, idx) => (
                        <Coins key={idx} className="h-5 w-5 text-primary" />
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {supplier.priceRange}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Specialties */}
                <div>
                  <p className="text-sm font-semibold mb-2 text-muted-foreground">
                    Specialties:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {supplier.specialties.map((specialty, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Order Info */}
                <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/40">
                  {supplier.minOrder && (
                    <div>
                      <p className="text-xs text-muted-foreground">Min Order</p>
                      <p className="font-semibold flex items-center gap-1">
                        <Package className="h-3 w-3" />
                        {supplier.minOrder}
                      </p>
                    </div>
                  )}
                  {supplier.deliveryTime && (
                    <div>
                      <p className="text-xs text-muted-foreground">Delivery</p>
                      <p className="font-semibold flex items-center gap-1">
                        <Truck className="h-3 w-3" />
                        {supplier.deliveryTime}
                      </p>
                    </div>
                  )}
                </div>

                {/* Contact */}
                {supplier.contact && (
                  <div className="space-y-1">
                    {supplier.contact.phone && (
                      <a
                        href={`tel:${supplier.contact.phone}`}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                      >
                        <Phone className="h-3 w-3" />
                        {supplier.contact.phone}
                      </a>
                    )}
                    {supplier.contact.email && (
                      <a
                        href={`mailto:${supplier.contact.email}`}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                      >
                        <Mail className="h-3 w-3" />
                        {supplier.contact.email}
                      </a>
                    )}
                  </div>
                )}

                {/* Notes */}
                {supplier.notes && (
                  <p className="text-sm text-muted-foreground italic flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-primary mt-0.5" />
                    <span>{supplier.notes}</span>
                  </p>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  {supplier.website && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => window.open(supplier.website, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Visit Website
                    </Button>
                  )}
                  {spendingSummary[supplier.id] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 bg-muted/60 hover:bg-muted"
                    >
                      <Euro className="h-4 w-4 mr-2" />
                      €{spendingSummary[supplier.id].totalSpending.toFixed(2)}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredSuppliers.length === 0 && (
          <div className="text-center py-20">
            <p className="text-2xl text-muted-foreground">No suppliers found</p>
            <p className="text-muted-foreground mt-2">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Documentation Link */}
        <Card className="mt-8 backdrop-blur-sm bg-card/70 border-2 border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  Full Supplier Documentation
                </h3>
                <p className="text-muted-foreground">
                  View complete supplier database with prices, URLs, and sourcing tips
                </p>
              </div>
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => window.open('/docs/SUPPLIERS_MUNICH.md', '_blank')}
              >
                Open Documentation
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
